import Avatar from "@material-ui/core/Avatar"
import Chip from "@material-ui/core/Chip"
import Tooltip from "@material-ui/core/Tooltip"
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles"
import { type CardViewModel } from "../../api/v2/board-view-model"
import { AvatarGroup } from "@material-ui/lab"
import { AlignLeft, Clock } from "react-feather"
import clsx from "clsx"

const getRandomBackgroundColor = (initials: string, colors: string[]) => {
  const charCode = initials.charCodeAt(0)
  const colorIndex = charCode % colors.length
  return colors[colorIndex] || colors[0]
}

export function CardFooter({ card }: { card: CardViewModel }) {
  const classes = useStyles()

  return (
    <div className={classes.footerRoot}>
      <CardResponsible card={card} />
      <div className={classes.footerLeft}>
        <CardSchedule card={card} />
        {card.hasDescription ? (
          <Tooltip title={card.description ?? ""} placement="top">
            <span className={classes.icon}>
              <AlignLeft size={16} />
            </span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

function CardResponsible({ card }: { card: CardViewModel }) {
  const classes = useStyles()
  const theme = useTheme()
  const colors = [
    theme.palette.info.main,
    theme.palette.secondary.dark,
    theme.palette.primary.main,
    theme.palette.warning.dark,
    theme.palette.error.dark,
  ]
  if (card.responsible.length === 0) return null
  return (
    <AvatarGroup max={3} spacing={"medium"} className={classes.avatarRoot}>
      {card.responsible.map((user) => (
        <Tooltip key={user.id} title={getTooltipForResponsible(user)} placement="top">
          <Avatar style={{ backgroundColor: getRandomBackgroundColor(user.initials, colors) }}>{user.initials}</Avatar>
        </Tooltip>
      ))}
    </AvatarGroup>
  )
}

function CardSchedule({ card }: { card: CardViewModel }) {
  const classes = useStyles()
  if (!card.formattedSchedule) return null
  const className = clsx(classes.dateChip, {
    overdue: card.isOverdue,
    dueSoon: card.isDueSoon,
  })
  return <Chip icon={<Clock />} label={card.formattedSchedule} size="small" className={className} />
}

function getTooltipForResponsible(user: CardViewModel["responsible"][number]) {
  let tooltip = user.name
  if (user.email) {
    tooltip += ` (${user.email})`
  }
  return tooltip
}

const useStyles = makeStyles((theme: Theme) => ({
  footerRoot: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1),
    height: 32,
  },
  footerLeft: {
    display: "flex",
    alignItems: "stretch",
    gap: 4,
    flexGrow: 1,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    color: theme.palette.text.secondary,
  },
  avatarRoot: {
    "& .MuiAvatarGroup-avatar": {
      width: 24,
      height: 24,
      fontSize: 10,
      fontWeight: 600,
      border: 0,
    },
  },
  dateChip: {
    borderRadius: 4,
    marginLeft: -4,
    marginRight: 4,
    fontSize: 12,
    fontWeight: 500,
    height: 20,
    background: "transparent",
    color: theme.palette.text.primary,
    "& .MuiChip-icon": {
      color: theme.palette.text.primary,
      height: 13,
      width: 13,
      marginRight: 0,
    },
    "&.overdue": {
      marginLeft: 0,
      background: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      "& .MuiChip-icon": {
        color: theme.palette.error.contrastText,
      },
    },
    "&.dueSoon": {
      marginLeft: 0,
      background: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      "& .MuiChip-icon": {
        color: theme.palette.warning.contrastText,
      },
    },
  },
}))
